import { useState } from 'react';
import { api } from '@/api/client';
import { ReviewResponse } from '@/api/types';
import { handleAppError } from '@/lib/errorUtils';
import { AIGenerationHookParams } from './ai/types';
import { APP_ERRORS } from '@/constants/errors';

export function useResumeAnalysis({
  workspaceId,
  selectedId,
  jobText,
  targetDetails,
  setStatus,
  setStatusMessage,
  recordUsage,
  setRightView,
  getSignal,
  setIsGenerating,
  scrollToResults,
}: AIGenerationHookParams) {
  const [reviewResult, setReviewResult] = useState<ReviewResponse | null>(null);

  const handleAnalyze = async (skipStateUpdates: boolean | any = false) => {
    const isSkip = skipStateUpdates === true;
    if (!selectedId || jobText.trim().length < 50) {
      if (!isSkip) {
        setStatus('error');
        setStatusMessage('Please enter a longer, meaningful job description (at least 50 characters) to analyze against.');
      }
      return null;
    }
    
    setIsGenerating(prev => ({ ...prev, analysis: true }));
    if (!isSkip) {
      setStatus('idle');
      setRightView('analysis');
      scrollToResults();
    }

    try {
      if (!isSkip) {
        await new Promise(r => setTimeout(r, 300));
      }
      const signal = isSkip ? getSignal('package') : getSignal('analysis');
      const result = await api.review(workspaceId, { resumeId: selectedId, jobText, ...targetDetails }, { signal });
      if (result.usage) {
        recordUsage(result.usage.cost, result.usage.promptTokens, result.usage.completionTokens);
      }
      
      if (!result?.review?.atsReport) {
        throw new Error(APP_ERRORS.ANALYSIS_FAILED.message);
      }

      if (result.review.atsReport.matchingScore === undefined && result.review.atsReport.estimatedScore === undefined) {
        // Fallback if missing
        result.review.atsReport.matchingScore = 0;
        result.review.atsReport.estimatedScore = 0;
      }

      setReviewResult(result);
      if (!isSkip) setStatusMessage('Analysis complete');
      return result;
    } catch (err: any) {
      if (err.name === 'AbortError') return null;
      if (!isSkip) {
        setStatus('error');
        setStatusMessage(handleAppError(err, 'Analysis failed'));
      }
      throw err;
    } finally {
      setIsGenerating(prev => ({ ...prev, analysis: false }));
    }
  };

  return { reviewResult, setReviewResult, handleAnalyze };
}
